import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import sentryIssuesClient, {
  ListSentryIssuesParams,
} from "../../../api/sentryIssues";
import ticketsClient from "../../../api/tickets";
import { Ticket } from "../../Tickets/types";
import { SENTRY_TICKET_LABEL } from "../sentryTicket";

const ISSUES_KEY = ["sentry-issues"];
const PROJECTS_KEY = ["sentry-projects"];

export const useSentryProjects = () =>
  useQuery({
    queryKey: PROJECTS_KEY,
    queryFn: () => sentryIssuesClient.listProjects(),
    // The project list changes when someone adds a Sentry project, not hourly.
    staleTime: 60 * 60 * 1000,
  });

export const useSentryIssues = (params: ListSentryIssuesParams) =>
  useQuery({
    queryKey: [...ISSUES_KEY, params],
    queryFn: () => sentryIssuesClient.list(params),
    // The API already caches Sentry responses; this keeps tab switches instant.
    staleTime: 30 * 1000,
  });

export const useSentryIssueDetails = (issueId: string | null) =>
  useQuery({
    queryKey: ["sentry-issue", issueId],
    queryFn: () => sentryIssuesClient.get(issueId as string),
    enabled: issueId !== null,
  });

type StatusAction = "resolve" | "ignore" | "unresolve";

/**
 * Status lives in Sentry, so there is no local copy to keep in step — the
 * mutation just invalidates the list and lets it refetch.
 */
export const useSetSentryIssueStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      issueId,
      action,
    }: {
      issueId: string;
      action: StatusAction;
    }) => sentryIssuesClient[action](issueId),
    onSuccess: (_data, { issueId }) => {
      queryClient.invalidateQueries({ queryKey: ISSUES_KEY });
      queryClient.invalidateQueries({ queryKey: ["sentry-issue", issueId] });
    },
  });
};

/** Prefix of every ticket lookup below, invalidated when a ticket is raised. */
export const SENTRY_TICKETS_KEY = ["sentry-issue-tickets"];

/**
 * Tickets already raised for an issue. A ticket made from Sentry quotes the
 * permalink in its description, which the ticket search matches on.
 */
export const useSentryIssueTickets = (permalink: string | null) =>
  useQuery({
    queryKey: [...SENTRY_TICKETS_KEY, permalink],
    queryFn: () =>
      ticketsClient.searchTickets({ query: permalink as string, limit: 5 }),
    enabled: permalink !== null,
    select: (page) => page.tickets,
  });

/** The API's cap on one ticket search page. */
const MAX_TAGGED_TICKETS = 500;

/**
 * Tickets tagged `sentry`, keyed by the Sentry permalink in their link field,
 * so the table can mark every row from one request. Newest first: past the
 * cap, the oldest tickets drop out of the map.
 */
export const useSentryTaggedTickets = () =>
  useQuery({
    queryKey: [...SENTRY_TICKETS_KEY, "tagged"],
    queryFn: () =>
      ticketsClient.searchTickets({
        limit: MAX_TAGGED_TICKETS,
        sort: { createdAt: -1 },
        filters: { labelConditions: [{ anyOf: [SENTRY_TICKET_LABEL] }] },
      }),
    staleTime: 60 * 1000,
    select: (page) => {
      const byPermalink = new Map<string, Ticket[]>();
      page.tickets.forEach((ticket) => {
        if (!ticket.url) return;
        byPermalink.set(ticket.url, [
          ...(byPermalink.get(ticket.url) ?? []),
          ticket,
        ]);
      });
      return byPermalink;
    },
  });
